import React, { useEffect, useMemo, useRef } from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { IMeetingPresenterGroupDTO } from '../../../../../../../api/custom-pages.dao';
import { CustomPageFormViewModel } from './custom-page.viewmodel';
import { PRESENTER_GROUPS, PRESENTER_GROUPS_FILTERS } from '../../../../../../../modules/passport-pages/config';
import { PresenterGroupsMultiSelectButton } from '../../../../../../global/multi-select/presenter-groups-multi-select-button';
import { addText, cancelText, REMOVE_TEXT } from '../../../../../../../constants/common-labels.constants';
import './presenter-groups-container.scss';

export interface IPresenterGroupsContainerProps {
    pageVm: CustomPageFormViewModel;
    meetingPresenterGroups: IMeetingPresenterGroupDTO[];
    maxFiltersCount: number;
}

export const PresenterGroupsContainer = mobxReact.observer(({
    pageVm,
    meetingPresenterGroups,
    maxFiltersCount,
}: IPresenterGroupsContainerProps) => {
    const {
        value: meetingPresenterGroupsValue,
        touched: meetingPresenterGroupsFiltersTouched
    } = pageVm.getFieldByKey(PRESENTER_GROUPS);
    const meetingPresenterGroupsFiltersValue =
        (pageVm.getFieldByKey(PRESENTER_GROUPS_FILTERS).value as string[]) || [];

    const items = { meetingPresenterGroups: meetingPresenterGroups };
    const selectedMeetingIds = useMemo(
        () => Array.from(new Set((meetingPresenterGroupsFiltersValue || []).map(String))),
        [meetingPresenterGroupsFiltersValue]
    );
    const meetingIdToPresenters = useMemo(() => (
        new Map((meetingPresenterGroups || []).map(group => [String(group.meetingId), group.presenterNames || []]))
    ), [meetingPresenterGroups]);
    const presenterGroupsValue = (meetingPresenterGroupsValue as IMeetingPresenterGroupDTO[]) || [];

    const didInitSelectedIds = useRef(false);

    useEffect(() => {
        if (didInitSelectedIds.current) return;
        pageVm.initPresenterGroupsFilters(selectedMeetingIds, presenterGroupsValue);
        didInitSelectedIds.current = true;
    }, [pageVm, presenterGroupsValue, selectedMeetingIds]);

    return (
        <div className='list'>
            <div className='list__label-wrapper'>
                <div className='list__label'>
                    <FormattedMessage
                        id='oe.input.presenterGroups'
                        description='Input label'
                        defaultMessage='Presenter Groups'
                    />
                </div>
            </div>
             { (() => {
                const renderedPresenterLists = new Set<string>();
                return presenterGroupsValue
                    .map(group => String(group.meetingId))
                    .map(meetingId => {
                        const presentersList = meetingIdToPresenters.get(meetingId) || [];
                        const presentersKey = presentersList.join(', ');
                        if (!presentersList.length || renderedPresenterLists.has(presentersKey)) return null;
                        renderedPresenterLists.add(presentersKey);
                    return (
                    <div key={meetingId} className='access-filter list__wrapper p-left-5px'>
                        <div className='list__item overflow-ellipsis'>
                            <p className='overflow-ellipsis'>
                                {presentersKey}
                            </p>
                        </div>
                        <label
                            onClick={() => {
                                const meetingIdsToRemove = presenterGroupsValue
                                    .filter(group => {
                                        const groupPresenters = meetingIdToPresenters.get(String(group.meetingId)) || [];
                                        return groupPresenters.join(', ') === presentersKey;
                                    })
                                    .map(group => String(group.meetingId));
                                pageVm.removePresenterGroupsByMeetingIds(meetingIdsToRemove, presenterGroupsValue);
                            }}
                            className='list__wrapper__remove-label'
                        >
                            <FormattedMessage {...REMOVE_TEXT}/>
                        </label>
                    </div>
                );
                });
            })()}

            {selectedMeetingIds.length < maxFiltersCount && <PresenterGroupsMultiSelectButton
                className='default-button__add-button'
                items={items}
                text={<FormattedMessage
                    id='oe.button.label.addPresenter'
                    description='Button label'
                    defaultMessage='+ Add Presenter' />}
                applyButtonText={addText()}
                resetButtonText={cancelText()}
                onChange={selectedIds => pageVm.addPresenterGroupsFilters(
                    selectedIds,
                    meetingPresenterGroups || [],
                    meetingPresenterGroupsFiltersTouched
                )}
                value={selectedMeetingIds}
            />}
            {
                (!pageVm.meetingPresenterGroupsFilters)
                    ? (
                        <span className='oe-form-field__error'>
                            <FormattedMessage
                                id='oe.validation.error.restrictFiltersErrorMessage'
                                defaultMessage='Please add at least one filter'
                            />
                        </span>
                    )
                    : null
            }
        </div>
    );
});
